import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const API_BASE = process.env.REACT_APP_API_BASE;

// Normalize an entry_date value to a YYYY-MM-DD string (local)
const toDateKey = (value) => {
    if (!value) return "";
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
    const d = new Date(value);
    if (isNaN(d.getTime())) return "";
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${d.getFullYear()}-${m}-${day}`;
};

const todayKey = () => toDateKey(new Date());

const addDays = (key, delta) => {
    const d = new Date(key + "T00:00:00");
    d.setDate(d.getDate() + delta);
    return toDateKey(d);
};

function StatCard({ icon, label, value, color, sub }) {
    return (
        <div className="col-6 col-md-4 col-xl-3">
            <div className="card border-0 shadow-sm h-100 stat-card" style={{ borderRadius: 16, background: "#fff" }}>
                <div className="card-body d-flex align-items-center stat-card-body">
                    <div
                        className="d-flex align-items-center justify-content-center me-3 stat-card-icon"
                        style={{ borderRadius: 14, background: `${color}22`, color, flexShrink: 0 }}
                    >
                        <i className={`bi ${icon}`}></i>
                    </div>
                    <div className="text-truncate" style={{ minWidth: 0 }}>
                        <div className="fw-bold stat-card-value" style={{ color: "#2d3a4a", lineHeight: 1.1 }}>{value}</div>
                        <div className="text-muted text-truncate stat-card-label">{label}</div>
                        {sub && <div className="stat-card-sub" style={{ color, fontWeight: 600 }}>{sub}</div>}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function UserDashboard({ email, displayName, premiumExpiry, userType, setView }) {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchEntries = async () => {
            setLoading(true);
            try {
                const token = localStorage.getItem("token");
                const res = await axios.get(`${API_BASE}/api/sadhana/entries/${email}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setEntries(Array.isArray(res.data) ? res.data : []);
            } catch (err) {
                console.error("Error fetching entries:", err);
                setEntries([]);
            } finally {
                setLoading(false);
            }
        };
        if (email) fetchEntries();
    }, [email]);

    const stats = useMemo(() => {
        const num = (v) => (v == null || v === "" || isNaN(Number(v)) ? 0 : Number(v));
        const dateSet = new Set(entries.map((e) => toDateKey(e.entry_date)).filter(Boolean));

        // Current streak (counts today if present, otherwise starts from yesterday)
        let streak = 0;
        let cursor = dateSet.has(todayKey()) ? todayKey() : addDays(todayKey(), -1);
        while (dateSet.has(cursor)) {
            streak += 1;
            cursor = addDays(cursor, -1);
        }

        // This month
        const ym = todayKey().slice(0, 7);
        const monthEntries = entries.filter((e) => toDateKey(e.entry_date).startsWith(ym));

        const totalRounds = entries.reduce((s, e) => s + num(e.chanting_rounds), 0);
        const monthRounds = monthEntries.reduce((s, e) => s + num(e.chanting_rounds), 0);
        const avgRounds = monthEntries.length ? Math.round(monthRounds / monthEntries.length) : 0;
        const monthReading = monthEntries.reduce((s, e) => s + num(e.reading_time), 0);
        const monthHearing = monthEntries.reduce((s, e) => s + num(e.hearing_time), 0);
        const monthService = monthEntries.reduce((s, e) => s + num(e.service_time), 0);

        const sortedDates = [...dateSet].sort();
        const lastEntry = sortedDates.length ? sortedDates[sortedDates.length - 1] : null;

        // Last 14 days chanting rounds (sum per day)
        const roundsByDay = {};
        entries.forEach((e) => {
            const k = toDateKey(e.entry_date);
            if (k) roundsByDay[k] = (roundsByDay[k] || 0) + num(e.chanting_rounds);
        });
        const trend = [];
        for (let i = 13; i >= 0; i--) {
            const k = addDays(todayKey(), -i);
            trend.push({ key: k, rounds: roundsByDay[k] || 0 });
        }

        return {
            total: entries.length,
            streak,
            monthCount: monthEntries.length,
            avgRounds,
            totalRounds,
            monthReading,
            monthHearing,
            monthService,
            lastEntry,
            trend,
        };
    }, [entries]);

    const isPremiumActive = premiumExpiry && !isNaN(new Date(premiumExpiry)) && new Date(premiumExpiry) >= new Date();

    const trendChart = {
        labels: stats.trend.map((t) => {
            const d = new Date(t.key + "T00:00:00");
            return `${d.getDate()}/${d.getMonth() + 1}`;
        }),
        datasets: [
            {
                label: "Chanting rounds",
                data: stats.trend.map((t) => t.rounds),
                borderColor: "#3d5a1a",
                backgroundColor: "rgba(61,90,26,0.15)",
                fill: true,
                tension: 0.35,
                pointRadius: 3,
            },
        ],
    };
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: "bottom" } },
        scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
    };

    const formatDate = (key) => {
        if (!key) return "—";
        const d = new Date(key + "T00:00:00");
        return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 260 }}>
                <div className="spinner-border text-success" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    const enteredToday = entries.some((e) => toDateKey(e.entry_date) === todayKey());

    return (
        <div className="container-fluid px-2 px-md-4 mt-3" style={{ maxWidth: 1200 }}>
            <style>{`
                .stat-card-icon { width: 50px; height: 50px; font-size: 24px; }
                .stat-card-value { font-size: 1.5rem; }
                .stat-card-label { font-size: 0.88rem; }
                .stat-card-sub { font-size: 0.78rem; }
                @media (max-width: 575.98px) {
                    .stat-card-body { padding: 0.6rem; }
                    .stat-card-icon { width: 38px; height: 38px; font-size: 18px; border-radius: 11px !important; }
                    .stat-card-icon { margin-right: 0.6rem !important; }
                    .stat-card-value { font-size: 1.15rem; }
                    .stat-card-label { font-size: 0.72rem; }
                    .stat-card-sub { font-size: 0.66rem; }
                }
                @media (max-width: 360px) {
                    .stat-card-icon { width: 32px; height: 32px; font-size: 16px; }
                    .stat-card-value { font-size: 1rem; }
                    .stat-card-label { font-size: 0.66rem; }
                    .stat-card-sub { font-size: 0.6rem; }
                }
            `}</style>
            {/* Greeting + today's CTA */}
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: 18, background: "linear-gradient(135deg,#fff8f3,#eef6e8)" }}>
                <div className="card-body d-flex flex-wrap justify-content-between align-items-center">
                    <div>
                        <h4 className="fw-bold mb-1" style={{ color: "#3d5a1a" }}>
                            Hare Krishna{displayName ? `, ${displayName}` : ""} 🙏
                        </h4>
                        <div className="text-muted" style={{ fontSize: "0.95rem" }}>
                            {enteredToday
                                ? "You've recorded your sadhana today. Keep up the great work!"
                                : "You haven't recorded your sadhana today yet."}
                        </div>
                    </div>
                    <button
                        className={`btn ${enteredToday ? "btn-outline-success" : "btn-success"} fw-bold mt-2 mt-md-0`}
                        onClick={() => setView && setView("entry")}
                    >
                        <i className="bi bi-pencil-square me-1"></i>
                        {enteredToday ? "Add / Update Today" : "Enter Today's Sadhana"}
                    </button>
                </div>
            </div>

            {/* Stat cards */}
            <div className="row g-2 g-md-3">
                <StatCard icon="bi-fire" label="Current Streak" value={`${stats.streak}d`} color="#c0392b" sub={stats.streak > 0 ? "Keep it going!" : "Start today"} />
                <StatCard icon="bi-journal-check" label="Total Entries" value={stats.total} color="#3d5a1a" />
                <StatCard icon="bi-calendar-check" label="This Month" value={`${stats.monthCount} days`} color="#3b5998" />
                <StatCard icon="bi-infinity" label="Avg Rounds (month)" value={stats.avgRounds} color="#a05a2c" />
                <StatCard icon="bi-stars" label="Total Rounds" value={stats.totalRounds} color="#6f42c1" />
                <StatCard icon="bi-book" label="Reading (month)" value={`${stats.monthReading}m`} color="#17a2b8" />
                <StatCard icon="bi-headphones" label="Hearing (month)" value={`${stats.monthHearing}m`} color="#e83e8c" />
                <StatCard icon="bi-hand-thumbs-up" label="Service (month)" value={`${stats.monthService}m`} color="#20c997" />
            </div>

            <div className="row mt-2">
                {/* Chart */}
                <div className="col-12 col-lg-8 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 16 }}>
                        <div className="card-body">
                            <h6 className="fw-bold mb-3" style={{ color: "#3d5a1a" }}>Chanting Rounds — Last 14 Days</h6>
                            <div style={{ position: "relative", height: 280 }}>
                                <Line data={trendChart} options={chartOptions} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Side info */}
                <div className="col-12 col-lg-4 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 16 }}>
                        <div className="card-body">
                            <h6 className="fw-bold mb-3" style={{ color: "#3d5a1a" }}>At a Glance</h6>
                            <ul className="list-unstyled mb-3" style={{ fontSize: "0.95rem" }}>
                                <li className="d-flex justify-content-between py-1 border-bottom">
                                    <span className="text-muted">Last entry</span>
                                    <span className="fw-semibold">{formatDate(stats.lastEntry)}</span>
                                </li>
                                <li className="d-flex justify-content-between py-1 border-bottom">
                                    <span className="text-muted">Plan</span>
                                    <span className="fw-semibold text-capitalize">{userType || "—"}</span>
                                </li>
                                <li className="d-flex justify-content-between py-1">
                                    <span className="text-muted">Premium</span>
                                    <span className={`fw-semibold ${isPremiumActive ? "text-success" : "text-danger"}`}>
                                        {isPremiumActive ? `Active till ${formatDate(toDateKey(premiumExpiry))}` : "Inactive"}
                                    </span>
                                </li>
                            </ul>
                            {!isPremiumActive && (
                                <button className="btn btn-danger w-100 fw-bold" onClick={() => setView && setView("upgradePremium")}>
                                    <i className="bi bi-unlock me-1"></i> Unlock Premium
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Quick actions */}
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: 16 }}>
                <div className="card-body">
                    <h6 className="fw-bold mb-3" style={{ color: "#3d5a1a" }}>Quick Actions</h6>
                    <div className="d-flex flex-wrap gap-2">
                        <button className="btn btn-success" onClick={() => setView && setView("entry")}>
                            <i className="bi bi-pencil-square me-1"></i> New Entry
                        </button>
                        <button className="btn btn-outline-success" onClick={() => setView && setView("download")}>
                            <i className="bi bi-list-check me-1"></i> View Entries
                        </button>
                        <button className="btn btn-outline-primary" onClick={() => setView && setView("sadhanaReports")}>
                            <i className="bi bi-bar-chart me-1"></i> Sadhana Chart
                        </button>
                        <button className="btn btn-outline-warning" onClick={() => setView && setView("sadhanaTemplate")}>
                            <i className="bi bi-file-earmark-text me-1"></i> Template
                        </button>
                        <button className="btn btn-outline-secondary" onClick={() => setView && setView("todoList")}>
                            <i className="bi bi-calendar-event me-1"></i> Plan Your Day
                        </button>
                        <button className="btn btn-outline-info" onClick={() => setView && setView("quickLinkSteps")}>
                            <i className="bi bi-grid me-1"></i> All Features
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
